/**
 * Live Feed op de Chats-pagina: samenvoegen en terugbladeren.
 *
 * 26-09-2026 (Jordy — "de live feed laadt maar een beperkt aantal regels en
 * scrollt niet verder door"). De pagina haalde elke 2,5 s de nieuwste 40
 * regels op en VERVING daar de lijst mee; ouder laden bestond niet. Voor
 * beheer kostten posting-events ook nog plaatsen in die 40, die daarna op het
 * scherm werden weggefilterd.
 *
 * Nu zoals het oude dashboard het deed: de kop wordt bij elke verversing
 * SAMENGEVOEGD met wat er al staat, en onderaan scrollen haalt de volgende
 * bladzijde op met `before=<oudste tijdstip>`. De backend kende `before` al.
 *
 * Alleen pure functies hier, zodat de test ze zonder browser kan draaien.
 */

export interface FeedRow {
  id?: unknown
  type?: unknown
  timestamp?: unknown
  mass_message_log_id?: number | null
}

/** Eerste bladzijde en elke verversing van de kop. */
export const FEED_EERSTE = 60
/** Elke oudere bladzijde. 27-09: van 100 naar 300 — zeven dagen terug was
 *  anders zo'n 27 keer tot onderaan scrollen. */
export const FEED_OUDER = 300
/** De knop "Show 7 days" laadt in één keer door tot zo ver terug. */
export const FEED_KNOP_DAGEN = 7
/** Hoeveel bladzijden de knop hooguit ophaalt (300 × 40 = 12.000 regels). */
export const FEED_KNOP_MAX_BLADZIJDEN = 40
/** Verder terug dan dit bladert de feed niet (zoals oud). */
export const FEED_DAGEN = 90

/** Eén regel, één sleutel: een massabericht telt één keer, hoeveel fans ook. */
export function feedKey(r: FeedRow): string {
  if (r.mass_message_log_id != null) return `mass_message:${r.mass_message_log_id}`
  if (r.type === 'mass_message') return `mass_message:${String(r.id ?? '')}`
  return `${String(r.type ?? '')}:${String(r.id ?? r.timestamp ?? '')}`
}

function tijd(r: FeedRow): number {
  const t = Date.parse(String(r.timestamp ?? ''))
  return Number.isNaN(t) ? 0 : t
}

/** Nieuwste eerst, zonder dubbelen; een nieuwere versie van een regel wint. */
export function mergeFeed<T extends FeedRow>(huidig: T[], nieuw: T[]): T[] {
  const per = new Map<string, T>()
  for (const r of huidig) per.set(feedKey(r), r)
  for (const r of nieuw) per.set(feedKey(r), r)
  return [...per.values()].sort((a, b) => tijd(b) - tijd(a))
}

/** Hoeveel regels uit `nieuw` staan er nog niet in `huidig`? */
export function nieuweRegels(huidig: FeedRow[], nieuw: FeedRow[]): number {
  const bekend = new Set(huidig.map(feedKey))
  return nieuw.filter((r) => !bekend.has(feedKey(r))).length
}

/**
 * De URL voor één bladzijde. Beheer vraagt alleen berichten op, zodat
 * posting-events geen plaatsen meer innemen; het platform filtert de server.
 */
export function feedUrl(
  feed: 'dashboard' | 'chatter',
  platform: string,
  sinds: string,
  before?: string,
  /** 06-10-2026: het modelfilter van de Chats-pagina (één slug); de server filtert. */
  model?: string,
): string {
  const q = new URLSearchParams()
  q.set('limit', String(before ? FEED_OUDER : FEED_EERSTE))
  q.set('since', sinds)
  if (before) q.set('before', before)
  if (platform && platform !== 'all') q.set('platform', platform)
  if (model) q.set('model_slug', model)
  if (feed === 'dashboard') q.set('feed_type', 'messages')
  return `/api/admin/${feed}/activity?${q.toString()}`
}

/** Reikt de lijst al tot vóór `grens` (ms)? Dan hoeft de knop niet verder. */
export function reiktTot(rows: FeedRow[], grens: number): boolean {
  const oudste = rows[rows.length - 1]
  if (!oudste) return false
  const t = Date.parse(String(oudste.timestamp ?? ''))
  return !Number.isNaN(t) && t <= grens
}

/**
 * De kop van de feed verversen: de nieuwe eerste bladzijde is LEIDEND voor de
 * tijdspanne die hij beslaat.
 *
 * 27-09-2026 (Jordy — "alle berichten komen dubbel binnen"). De sync van oud
 * zet soms heel even een tweede rij voor hetzelfde fanbericht neer; binnen een
 * sync-ronde (~1 min) voegt de opruiming die samen en verdwijnt de rij. Omdat
 * de feed sinds 26-09 samenvoegt in plaats van vervangt, bleef zo'n rij in een
 * open tabblad staan tot iemand herlaadde. Nu valt een regel binnen de
 * tijdspanne van de nieuwe kop weg als de server hem niet meer teruggeeft.
 * Oudere, doorgebladerde regels blijven ongemoeid.
 */
export function vervangKop<T extends FeedRow>(huidig: T[], kop: T[]): T[] {
  if (kop.length === 0) return mergeFeed(huidig, kop)
  const grens = Math.min(...kop.map(tijd))
  const inKop = new Set(kop.map(feedKey))
  const blijft = huidig.filter((r) => tijd(r) < grens || inKop.has(feedKey(r)))
  return mergeFeed(blijft, kop)
}

/**
 * 20-10-2026 (Jordy): een bundel (meerdere foto's/video's in één bericht, zelfde
 * `bundle_id` in één gesprek) is in de Live Feed ÉÉN regel, net als in het gesprek.
 * Groepeert over alle geladen regels, dus ook als de onderdelen in verschillende
 * ophaalrondes binnenkwamen. De regel met het bijschrift blijft staan (anders de
 * eerste); `bundle_count` = het aantal onderdelen. Alleen weergave.
 */
export function groepeerBundels<T extends FeedRow & {
  bundle_id?: string | null; conversation_id?: number | null; message?: string | null; bundle_count?: number | null
}>(rows: T[]): T[] {
  const groep = new Map<string, T[]>()
  for (const r of rows) {
    const b = String(r.bundle_id ?? '').trim()
    if (!b) continue
    const k = `${r.conversation_id ?? ''}:${b}`
    const l = groep.get(k)
    if (l) l.push(r)
    else groep.set(k, [r])
  }
  if (!groep.size) return rows
  const bijschrift = (r: T) => {
    const t = String(r.message ?? '').trim()
    return !!t && !(t.startsWith('[') && t.endsWith(']'))
  }
  const gehad = new Set<string>()
  const uit: T[] = []
  for (const r of rows) {
    const b = String(r.bundle_id ?? '').trim()
    if (!b) { uit.push(r); continue }
    const k = `${r.conversation_id ?? ''}:${b}`
    const leden = groep.get(k) ?? [r]
    if (leden.length < 2) { uit.push(r); continue }
    if (gehad.has(k)) continue
    gehad.add(k)
    const basis = leden.find(bijschrift) ?? r
    uit.push({ ...basis, timestamp: r.timestamp, bundle_count: leden.length })
  }
  return uit
}
